import React from 'react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';

import { Display, Flex, Image, ImageProps } from '@rover/kibble/core';
import type { StringSystemType } from '@rover/kibble/core/styledSystemPropTypes';
import MQ from '@rover/kibble/styles/MediaQuery';
import LandingHero from '@rover/react-lib/src/pages/landing/components/LandingHero';
import LandingSearchForm, {
  Props as LandingSearchFormProps,
} from '@rover/react-lib/src/pages/landing/components/LandingSearchForm';
import { PetType } from '@rover/types/src/Pet';

import {
  boardingCatHeroImage,
  boardingDogHeroImage,
  boardingPetHeroImage,
  doggyDayCareHeroImage,
  dogWalkingHeroImage,
  dropInCatHeroImage,
  dropInDogHeroImage,
  dropInPetHeroImage,
  houseSittingCatHeroImage,
  houseSittingDogHeroImage,
  houseSittingPetHeroImage,
} from '../images';

// Either an explicit `heading` is supplied, or `serviceTypeName` + `locationName` are
// supplied so the component can build the default heading. Requiring one of the two
// prevents rendering an empty heading from missing data.
type HeadingProps =
  | { heading: React.ReactNode | string; serviceTypeName?: string; locationName?: string }
  | { heading?: undefined; serviceTypeName: string; locationName: string };

// Most SEO location pages search through `LandingSearchForm`. A page whose service has its
// own search endpoint (grooming) passes `searchForm` instead and supplies its own form.
type SearchFormProps =
  | ({ searchForm?: undefined } & LandingSearchFormProps)
  | ({ searchForm: React.ReactNode } & Partial<LandingSearchFormProps>);

type Props = {
  desktopImage?: string;
  petType?: string | null;
  internalServiceTypeSlug?: string;
  subheading: string;
  showTrustpilot?: boolean;
  /** Caps the hero image height. Defaults to 400px. */
  imageMaxHeight?: string;
  /**
   * Width-to-height ratio of the hero art, so the image reserves its space before it
   * loads. Defaults to the 1.5 the shared service art is exported at; pass the real
   * ratio for art exported at another shape or it will letterbox inside a 1.5 box.
   */
  imageAspectRatio?: number;
  /**
   * Where the hero image sits in the row. 'center' (default) gives it half the row and
   * centres the art. 'right' gives it whatever the text column leaves over and pins the
   * art to the right edge, so it reads as larger and further right.
   */
  imageAlignment?: 'center' | 'right';
} & HeadingProps &
  SearchFormProps;

// Picks the cat/dog/pet-agnostic variant of a service type's art based on pet type. `petType`
// is only ever 'cat' or 'dog' for a real pet-specific page; anything else (unset, a generic
// pet-sitting page with no specific pet type) gets the pet-agnostic variant.
const getPetVariant = (
  petType: string | null | undefined,
  catImage: string,
  dogImage: string,
  petImage: string
): string => {
  if (petType === PetType.CAT) {
    return catImage;
  }
  if (petType === PetType.DOG) {
    return dogImage;
  }
  return petImage;
};

// Selects the hero image based on the pet type and the internal service type, mirroring the
// images the per-service SEO location pages use. Falls back to the boarding image.
export const getHeroImage = (
  petType: string | null | undefined,
  internalServiceTypeSlug?: string
): string => {
  switch (internalServiceTypeSlug) {
    case 'dog-walking':
      return dogWalkingHeroImage;
    case 'doggy-day-care':
      return doggyDayCareHeroImage;
    case 'overnight-traveling':
      return getPetVariant(
        petType,
        houseSittingCatHeroImage,
        houseSittingDogHeroImage,
        houseSittingPetHeroImage
      );
    case 'drop-in':
      return getPetVariant(petType, dropInCatHeroImage, dropInDogHeroImage, dropInPetHeroImage);
    default:
      return getPetVariant(
        petType,
        boardingCatHeroImage,
        boardingDogHeroImage,
        boardingPetHeroImage
      );
  }
};

// 600px is the shared service art's native width, so the image renders 1:1 instead of being downscaled
// by a 50% column. Only the widest breakpoint's 1140px Section has room for it alongside the
// 416px text column; below that the two columns stay even halves.
export const HERO_IMAGE_FLEX_BASIS: StringSystemType = ['100%', '50%', '50%', '600px'];

const DEFAULT_IMAGE_MAX_HEIGHT = '400px';

// Matches the 600x400 the shared per-service hero art is exported at. Art with another
// shape passes its own ratio through `imageAspectRatio`.
const DEFAULT_IMAGE_ASPECT_RATIO = 1.5;

// styled image is required because kibble images don't support aspect-ratio yet
const HeroImage = styled(Image)<{
  $maxHeight: string;
  $objectPosition: string;
  $aspectRatio: number;
}>`
  // we use aspect-ratio instead of using height auto to reduce CLS
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio};
  // limit the image height to avoid it being the tallest thing in the Hero
  max-height: ${({ $maxHeight }) => $maxHeight};
  // the art is letterboxed inside the box above; this decides which edge it hugs
  object-position: ${({ $objectPosition }) => $objectPosition};
`;

type HeroImageWrapperProps = ImageProps & {
  maxHeight?: string;
  objectPosition?: string;
  aspectRatio?: number;
};

export const HeroImageWrapper: React.FC<HeroImageWrapperProps> = ({
  description,
  height,
  width,
  src,
  resizeMethod,
  webProps,
  maxHeight = DEFAULT_IMAGE_MAX_HEIGHT,
  objectPosition = 'center',
  aspectRatio = DEFAULT_IMAGE_ASPECT_RATIO,
}) => {
  return (
    <>
      <Helmet>
        <link
          media={MQ.NOT_XS.getBreakpointString()}
          rel="preload"
          as="image"
          href={src as string}
          fetchPriority="high"
        />
      </Helmet>
      <HeroImage
        description={description}
        height={height}
        width={width}
        src={src}
        resizeMethod={resizeMethod}
        webProps={webProps}
        $maxHeight={maxHeight}
        $objectPosition={objectPosition}
        $aspectRatio={aspectRatio}
      />
    </>
  );
};

const SeoLocationHero: React.FC<Props> = ({
  desktopImage,
  petType,
  internalServiceTypeSlug,
  serviceTypeName,
  locationName,
  heading,
  subheading,
  showTrustpilot = false,
  imageMaxHeight,
  imageAspectRatio,
  imageAlignment = 'center',
  searchForm,
  ...landingSearchFormProps
}) => {
  const heroImage = desktopImage ?? getHeroImage(petType, internalServiceTypeSlug);
  const alignImageRight = imageAlignment === 'right';

  const heroHeading = heading ?? (
    <>
      {serviceTypeName}{' '}
      <Display webProps={{ as: 'span', responsive: false }} size="500" textColor="display">
        {locationName}
      </Display>
    </>
  );

  return (
    <Flex
      position="relative"
      pt="12x"
      pb="6x"
      justifyContent={['center', 'space-between']}
      alignItems="flex-start"
    >
      <Flex
        width="100%"
        maxWidth="416px"
        justifyContent="center"
        flexDirection="column"
        flexBasis={['100%', '50%', '416px']}
      >
        <LandingHero.Display>{heroHeading}</LandingHero.Display>
        <LandingHero.Text>{subheading}</LandingHero.Text>
        {searchForm ?? (
          <LandingSearchForm {...(landingSearchFormProps as LandingSearchFormProps)} />
        )}
        {showTrustpilot && <LandingHero.TrustPilot />}
      </Flex>
      <Flex
        display={['none', 'flex']}
        alignSelf="center"
        alignItems="center"
        justifyContent={alignImageRight ? 'flex-end' : 'center'}
        flexBasis={alignImageRight ? undefined : HERO_IMAGE_FLEX_BASIS}
        flex={alignImageRight ? '1' : undefined}
      >
        <HeroImageWrapper
          description=""
          height="auto"
          width="100%"
          src={heroImage}
          resizeMethod="contain"
          maxHeight={imageMaxHeight}
          aspectRatio={imageAspectRatio}
          objectPosition={alignImageRight ? 'right' : 'center'}
          webProps={{
            eager: true,
            // @ts-expect-error fetchPriority is not in ImageWebOnlyProps, but is a valid img attribute
            fetchPriority: 'high',
          }}
        />
      </Flex>
    </Flex>
  );
};

export default SeoLocationHero;
